import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Empty, Flex, Spin } from 'antd';
import { getSortedIndividualsForDisplay } from '@api/btlApplicationForm/borrowerHelpers';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { IndividualCard } from '@pages/BtlApplicationFormPage/Applicants/Individual/IndividualCard';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { isOoApplicantIndividualFormEditable } from './CaseApplicantIndividualPage';

const MAX_INDIVIDUALS = 4;
export const OO_CREATE_INDIVIDUAL_SEARCH_PARAM = 'createIndividual';

interface CaseApplicantsListProps {
  case?: BtlApplication;
  isCaseLoading: boolean;
}

export function CaseApplicantsList({ case: caseData, isCaseLoading }: CaseApplicantsListProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  if (isCaseLoading || !caseData) {
    return <Spin size='large' />;
  }

  const individualsToSort = getSortedIndividualsForDisplay(caseData.borrower);
  const isEditable = isOoApplicantIndividualFormEditable(caseData);

  const handleViewIndividual = (individualId: string) => {
    const searchParams = new URLSearchParams({
      section: CaseSidebarItemId.APPLICANTS,
      individualId,
    });
    navigate({ search: `?${searchParams.toString()}` });
  };

  const handleAddIndividual = () => {
    const searchParams = new URLSearchParams({
      section: CaseSidebarItemId.APPLICANTS,
      [OO_CREATE_INDIVIDUAL_SEARCH_PARAM]: '1',
    });
    navigate({ search: `?${searchParams.toString()}` });
  };

  const addIndividualCard = isEditable && individualsToSort.length < MAX_INDIVIDUALS && (
    <Card
      actions={[
        <Button className='w-full' key='add' type='link' icon={<PlusOutlined />} onClick={handleAddIndividual}>
          {formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addIndividual.label' })}
        </Button>,
      ]}
    >
      <Empty
        description={formatMessage({
          id:
            individualsToSort.length === 0
              ? 'btlApplicationForm.APPLICANTS_INDIVIDUAL.noIndividual.label'
              : 'btlApplicationForm.APPLICANTS_INDIVIDUAL.addAnotherIndividual.label',
        })}
      />
    </Card>
  );

  return (
    <Flex gap={16} vertical>
      <h3 className='mb-0'>{formatMessage({ id: 'btlApplicationForm.APPLICANTS_INDIVIDUAL.individual.label' })}</h3>
      <Flex gap={16} wrap align='start'>
        {individualsToSort.map(individual => (
          <IndividualCard
            key={individual.id}
            individual={individual}
            applicationId={caseData.id}
            borrowerId={caseData.borrower.id}
            showRemoveAction={false}
            refetch={() => undefined}
            isEditable={isEditable}
            onViewIndividual={handleViewIndividual}
            listVariant='oo'
          />
        ))}
        {addIndividualCard}
      </Flex>
    </Flex>
  );
}
